import { Capacitor, registerPlugin } from '@capacitor/core';
import { getLocale, getT } from '@/i18n';
import { isNativeApp } from '@/lib/platform';

/**
 * BIOMETRIKUS APP-ZÁR — a felület elé tett zár.
 *
 * ⚠️ AMIT VÉD, ÉS AMIT NEM (Geri döntése, 2026-09-17). Ez a zár a FELÜLET elé
 * áll: a Firebase-munkamenet ugyanúgy él tovább, mint eddig. Véd az ellen, hogy
 * aki felveszi a feloldott telefonodat, belenézzen a GRUNDO-dba. NEM véd az
 * ellen, hogy valaki rootolt készüléken kiolvassa az app tárolóját — ahhoz a
 * frissítő tokent kellene a Keystore/Keychain kulcsa mögé tenni, ami lényegesen
 * nagyobb változás, és a hibája magát a bejelentkezést rontaná el.
 *
 * ⚠️ A BEÁLLÍTÁS ESZKÖZHÖZ KÖTÖTT, nem a fiókhoz — `localStorage`, mint a
 * grafikai beállítás és az élő mérés. Két oka van: a zár azt a KÉSZÜLÉKET
 * védi, amin be van kapcsolva (a másik telefonodon más biometria van, vagy
 * semmilyen), és a szerveren tárolva egy kijelentkezett kliens nem is tudná
 * elolvasni, mielőtt eldönti, kérjen-e feloldást.
 *
 * ⚠️ CSAK HIDEGINDÍTÁSKOR KÉRDEZ. Háttérből visszatérve sosem — ezt Geri
 * választotta: a gyakori kérdezgetés miatt a legtöbben inkább kikapcsolnák az
 * egészet, és egy kikapcsolt zár nulla védelem.
 */

export type BiometricFailure =
  | 'cancelled'
  | 'failed'
  | 'no_hardware'
  | 'none_enrolled'
  | 'not_installed'
  | 'unavailable';

interface BiometricPlugin {
  isAvailable(): Promise<{ available: boolean; reason: string }>;
  authenticate(options: { title?: string; subtitle?: string; locale?: 'hu' | 'en' }): Promise<void>;
}

const Biometric = registerPlugin<BiometricPlugin>('Biometric');

const STORAGE_KEY = 'grundo.appLock.v1';

/** Be van-e kapcsolva a zár ezen az eszközön. */
export function appLockEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'on';
  } catch {
    /* Privát mód, letiltott tároló: ilyenkor nincs zár. Inkább ne zárjunk,
       mint hogy egy megnyithatatlan képernyő mögé rekesszük a felhasználót. */
    return false;
  }
}

export function setAppLockEnabled(value: boolean): void {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, 'on');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // A beállítás elvesztése kellemetlen, de nem hiba — a kapcsoló így is működik.
  }
}

/**
 * Van-e egyáltalán mivel feloldani ezen az eszközön.
 *
 * ⚠️ A `not_installed` KÜLÖN OK, és drágán tanultuk meg (2026-09-17). A zár
 * mindkét platformon néma halott volt, mert a plugin natív bekötése kimaradt
 * — a felület viszont erre is, a „nincs beállítva biometria" esetre is
 * ugyanazt a mondatot írta ki. Két teljes build-kör ment el azzal, hogy nem
 * lehetett megkülönböztetni a kettőt. A `Capacitor.isPluginAvailable()` a
 * natív híd `PluginHeaders` listájából olvas, tehát pontosan azt méri, hogy a
 * natív oldal bejelentette-e a plugint — ez a különbség egyetlen elérhető
 * bizonyítéka, és készülék nélkül nem pótolható.
 */
export async function biometricAvailable(): Promise<{ available: boolean; reason: string }> {
  if (!isNativeApp()) return { available: false, reason: 'unavailable' };
  if (!Capacitor.isPluginAvailable('Biometric')) {
    return { available: false, reason: 'not_installed' };
  }
  try {
    return await Biometric.isAvailable();
  } catch {
    return { available: false, reason: 'unavailable' };
  }
}

export class UnlockError extends Error {
  constructor(public readonly reason: BiometricFailure, message: string) {
    super(message);
  }
}

/**
 * ⚠️ SAJÁT, LOKALIZÁLT ÜZENETEK — a plugin szövegét SOSEM adjuk tovább a
 * felhasználónak. Az a rendszertől vagy a Capacitortól jön, angolul és
 * fejlesztői nyelven („Biometric plugin is not implemented on web"). A
 * `reason` kódot használjuk, ami mindkét platformon ugyanaz.
 */
function failureMessage(reason: BiometricFailure): string {
  const { errors } = getT().appLock;
  switch (reason) {
    case 'cancelled':
      return errors.cancelled;
    case 'failed':
      return errors.failed;
    case 'no_hardware':
      return errors.noHardware;
    case 'none_enrolled':
      return errors.noneEnrolled;
    case 'not_installed':
      return errors.notInstalled;
    case 'unavailable':
      return errors.unavailable;
  }
}

/** Feloldás kérése. Sikertelenség esetén `UnlockError`-ral száll el. */
export async function requestUnlock(subtitle: string): Promise<void> {
  try {
    await Biometric.authenticate({ title: getT().appLock.systemTitle, subtitle, locale: getLocale() });
  } catch (cause: unknown) {
    const code = (cause as { code?: string } | null)?.code;
    const reason = isFailure(code) ? code : 'failed';
    throw new UnlockError(reason, failureMessage(reason));
  }
}

function isFailure(code: unknown): code is BiometricFailure {
  return (
    code === 'cancelled'
    || code === 'failed'
    || code === 'no_hardware'
    || code === 'none_enrolled'
    || code === 'unavailable'
  );
}

/**
 * ⚠️ MODUL-SZINTŰ, SZÁNDÉKOSAN NEM `sessionStorage`. A kérdés az, hogy EZ A
 * BETÖLTÉS fel van-e már oldva. Egy tárolt „feloldva" jelző túlélné az app
 * újraindítását, tehát a hidegindítás pont nem kérdezne — vagyis a zár
 * semmit nem érne.
 */
let unlockedThisLaunch = false;

export function isUnlockedForThisLaunch(): boolean {
  return unlockedThisLaunch;
}

export function markUnlocked(): void {
  unlockedThisLaunch = true;
}

/**
 * Kell-e zárképernyő MOST? Csak natív appban, csak bekapcsolt zárnál, és csak
 * akkor, ha ez a betöltés még nincs feloldva.
 */
export function needsUnlock(): boolean {
  return isNativeApp() && appLockEnabled() && !unlockedThisLaunch;
}
